import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Box, Button, Card, CardContent, CardHeader, Container, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, MenuItem, TextField, Typography
} from "@mui/material";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import BreadCrumbs from "../components/Breadcrumbs";
import {
    createStoreItem,
    editStoreItem,
    getShopStore,
    deleteStoreItem
} from "../http/ShopAPI";

const ServerShopItems = observer(() => {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        name: "",
        game_code: "",
        image_url: "",
        image_file: null,
        description: "",
        base_price: 0,
        category_id: ""
    });
    const [shopStore, setShopStore] = useState({});
    const [loading, setLoading] = useState(true);

    const handleOpen = (item = null) => {
        if (item) {
            setEditing(item);
            setForm({ ...item });
        } else {
            setEditing(null);
            setForm({
                name: "",
                game_code: "",
                image_url: "",
                description: "",
                base_price: 0,
                category_id: ""
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditing(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Удалить этот товар?")) {
            try {
                await deleteStoreItem(id);
                await loadStoreData();
            } catch (err) {
                console.error("Ошибка при удалении:", err);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "image/png") {
            setForm(prev => ({ ...prev, image_file: file, image_url: "" }));
        }
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await editStoreItem(form);
            } else {
                await createStoreItem(form);
            }
            await loadStoreData();
            handleClose();
        } catch (err) {
            console.error(err);
        }
    };

    const loadStoreData = () => {
        setLoading(true);
        return getShopStore()
            .then(setShopStore)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadStoreData();
    }, []);

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs="" name="Список товаров магазина" />
            </Box>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={600}>Товары для магазинов</Typography>
                    <Button onClick={() => handleOpen()}>
                        <Plus className="w-4 h-4 mr-1" /> Добавить
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {shopStore.items && shopStore.items.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                                <Card>
                                    <CardHeader
                                        title={item.name}
                                        action={
                                            <>
                                                <IconButton onClick={() => handleOpen(item)}>
                                                    <Pencil className="w-4 h-4" />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete(item.id)}>
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </IconButton>
                                            </>
                                        }
                                    />

                                    {/* Превью картинки, если есть */}
                                    {item.image_url && (
                                        <Box
                                            component="img"
                                            src={item.image_url}
                                            alt={item.name}
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                                objectFit: 'contain',
                                                borderBottom: '1px solid #ddd',
                                            }}
                                        />
                                    )}

                                    <CardContent>
                                        <Typography variant="body2">Код: {item.game_code}</Typography>
                                        <Typography variant="body2">Цена: {item.base_price}</Typography>
                                        <Typography variant="body2">Категория: {item.category_name || "—"}</Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <DialogTitle>{editing ? "Редактировать товар" : "Создать товар"}</DialogTitle>
                    <DialogContent>
                        <TextField fullWidth label="Название" name="name" value={form.name} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Игровой код" name="game_code" value={form.game_code} onChange={handleChange} margin="normal" />
                        <TextField
                            fullWidth
                            label="Изображение (URL)"
                            name="image_url"
                            value={form.image_url}
                            onChange={handleChange}
                            margin="normal"
                        />
                        <Button component="label" variant="outlined" sx={{ mt: 2 }}>
                            Загрузить PNG
                            <input
                                type="file"
                                accept="image/png"
                                hidden
                                onChange={handleFileUpload}
                            />
                        </Button>
                        {form.image_file && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Загружен файл: {form.image_file.name}
                            </Typography>
                        )}
                        <TextField fullWidth label="Описание" name="description" value={form.description} onChange={handleChange} margin="normal" multiline rows={2} />
                        <TextField fullWidth label="Базовая цена" name="base_price" type="number" value={form.base_price} onChange={handleChange} margin="normal" />
                        <TextField
                            select
                            fullWidth
                            label="Категория"
                            name="category_id"
                            value={form.category_id}
                            onChange={handleChange}
                            margin="normal"
                        >
                            {shopStore.categories?.map(cat => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="ghost" onClick={handleClose}>Отмена</Button>
                        <Button onClick={handleSave}>{editing ? "Сохранить" : "Создать"}</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
});

export default ServerShopItems;